import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Siren } from "lucide-react";

// short data-URI beep (16-bit PCM, 8kHz) — 2 short pulses
const BEEP =
  "data:audio/wav;base64,UklGRnwGAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YVgGAAAAAAAAdXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1";
// Generate a real beep on the fly using WebAudio
function playBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const beep = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.001, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    };
    beep(880, 0, 0.18);
    beep(1175, 0.22, 0.24);
    beep(880, 0.5, 0.18);
    setTimeout(() => ctx.close?.(), 1200);
  } catch (_) { /* ignore */ }
}
function vibrate() {
  try { navigator.vibrate?.([200, 80, 200, 80, 400]); } catch (_) { /* ignore */ }
}

async function ensureNotificationPermission() {
  if (typeof Notification === "undefined") return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  try {
    const p = await Notification.requestPermission();
    return p === "granted";
  } catch (_) { return false; }
}

function showBrowserNotification(alert) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  try {
    const n = new Notification("🚨 تنبيه طارئ — مركز هابي داي", {
      body: `${alert.child_name}: ${alert.message?.slice(0, 120) || ""}`,
      tag: `emergency-${alert.id}`,
      requireInteraction: false,
    });
    n.onclick = () => {
      window.focus();
      window.location.href = "/emergency";
      n.close();
    };
    setTimeout(() => n.close(), 10000);
  } catch (_) { /* ignore */ }
}

export default function EmergencyBell() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const knownIdsRef = useRef(new Set());
  const seededRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    // Ask for browser notification permission once, on user session
    ensureNotificationPermission();
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/emergency");
        if (cancelled) return;
        const unack = data.filter((a) => !a.acknowledged);
        if (!seededRef.current) {
          data.forEach((a) => knownIdsRef.current.add(a.id));
          seededRef.current = true;
        } else {
          const fresh = unack.filter((a) => !knownIdsRef.current.has(a.id));
          if (fresh.length > 0) {
            fresh.forEach((a) => {
              knownIdsRef.current.add(a.id);
              showBrowserNotification(a);
            });
            playBeep();
            vibrate();
          }
        }
        data.forEach((a) => knownIdsRef.current.add(a.id));
        setPending(unack);
      } catch (_) { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);

  const count = pending.length;
  const active = count > 0 && location.pathname !== "/emergency";

  return (
    <button
      onClick={() => navigate("/emergency")}
      data-testid="emergency-bell"
      aria-label="تنبيهات الطوارئ"
      className={`relative w-11 h-11 rounded-xl border flex items-center justify-center transition-all ${
        active
          ? "bg-rose-600 border-rose-700 text-white animate-pulse"
          : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
      }`}
    >
      {active ? <Siren className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
      {count > 0 && (
        <span
          data-testid="emergency-bell-count"
          className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-rose-600 text-white text-[11px] font-black flex items-center justify-center ring-2 ring-white"
        >
          {count}
        </span>
      )}
    </button>
  );
}
